import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { QRCodeCanvas } from "qrcode.react";
import { CLUB_TIERS } from "@/lib/constants";
import { Wallet, Share2, Star, Sparkles, Ticket as TicketIcon } from "lucide-react";

export default function ClubCard() {
  const { qr } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/club/by-qr/${qr}`).then((r) => setD(r.data)).catch(() => setErr("Karte nicht gefunden"));
  }, [qr]);

  if (err) return <div className="min-h-screen bg-black text-[#EE9CA1] flex items-center justify-center">{err}</div>;
  if (!d) return <div className="min-h-screen bg-black text-[#A8A398] flex items-center justify-center">Lade Club-Karte …</div>;

  const tier = CLUB_TIERS.find((t) => t.v === d.tier) || CLUB_TIERS[0];
  const qrUrl = `${window.location.origin}/club/${d.member_qr || d.member_number}`;
  const topBenefits = (d.benefits || []).slice(0, 3);

  const openWallet = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/guests/by-qr/${d.member_qr}/wallet.pkpass`, "_blank");
  };
  const shareCard = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Movie House Club Karte", text: `${d.guest_name} — ${tier.l}`, url: qrUrl }); } catch { /* ignore */ }
    } else {
      try { await navigator.clipboard.writeText(qrUrl); } catch { /* ignore */ }
    }
  };

  return (
    <div className="h-screen w-screen bg-black text-[#F5F0E6] overflow-hidden relative flex items-center justify-center px-4">
      {/* Lieblingsfilm-Backdrop */}
      {d.favorite_movie_backdrop_url && (
        <>
          <img src={d.favorite_movie_backdrop_url} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }}
            className="absolute inset-0 w-full h-full object-cover opacity-40 scale-110" style={{ filter: "blur(30px) saturate(1.1)" }} />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/85" />
        </>
      )}
      {/* Samt-Grain */}
      <div className="absolute inset-0 opacity-[0.05] mix-blend-overlay pointer-events-none"
        style={{ backgroundImage: "radial-gradient(#D4AF37 1px, transparent 1px)", backgroundSize: "3px 3px" }} />
      {/* Weicher Tier-Glow oben */}
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full opacity-25 pointer-events-none blur-[100px]" style={{ background: tier.color }} />

      <div className="relative z-10 w-full max-w-sm">
        {/* Mini-Header */}
        <div className="text-center mb-3">
          <div className="font-heading text-2xl text-[#D4AF37] tracking-wide leading-none" style={{ textShadow: "0 0 24px rgba(212,175,55,0.35)" }}>
            {d.cinema_name}
          </div>
          <div className="text-[9px] mt-1 uppercase tracking-[0.4em] text-[#A8A398]">{d.slogan || "More Than Watching Movies"}</div>
        </div>

        {/* CLUB-KARTE */}
        <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#0F0F0F] via-[#0A0A0A] to-black shadow-[0_18px_60px_rgba(0,0,0,0.75)]" data-testid="club-card">
          {/* Doppelrand */}
          <div className="absolute inset-0 rounded-2xl border border-[#8B7328]/50 pointer-events-none" />
          <div className="absolute inset-[5px] rounded-xl border pointer-events-none" style={{ borderColor: tier.color, opacity: 0.7 }} />

          {/* Tier-Band oben */}
          <div className="h-2" style={{ background: `linear-gradient(90deg, transparent, ${tier.color}, transparent)` }} />

          <div className="p-5 pt-4">
            {/* Tier-Zeile */}
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[9px] uppercase tracking-[0.42em] text-[#A8A398]">Movie House Club</div>
                <div className="font-heading text-3xl mt-1 flex items-center gap-2 leading-none" style={{ color: tier.color }}>
                  <Star size={18} fill={tier.color} strokeWidth={0} />
                  {tier.l}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[9px] uppercase tracking-[0.35em] text-[#A8A398]">Besuche</div>
                <div className="font-mono text-2xl" style={{ color: tier.color }}>{d.visits_count}</div>
              </div>
            </div>

            {/* Name */}
            <div className="mt-3">
              <div className="font-heading text-xl leading-none truncate">{d.guest_name}</div>
              <div className="mt-1 flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#A8A398]">
                <span>Nr.</span>
                <span className="font-mono text-[#D4AF37]">{d.member_number}</span>
              </div>
            </div>

            {/* QR + Vorteile Row */}
            <div className="mt-4 grid grid-cols-[132px_1fr] gap-4 items-center">
              <div className="bg-[#F5F0E6] p-2 rounded-lg mx-auto shadow-lg">
                <QRCodeCanvas value={qrUrl} size={112} bgColor="#F5F0E6" fgColor="#0A0A0A" />
              </div>
              <div className="min-w-0">
                <div className="text-[9px] uppercase tracking-[0.35em] flex items-center gap-1" style={{ color: tier.color }}>
                  <Sparkles size={10} /> Deine Vorteile
                </div>
                <ul className="mt-2 space-y-1.5">
                  {topBenefits.map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-[11px] leading-snug">
                      <span className="mt-1 inline-block w-1 h-1 rounded-full shrink-0" style={{ background: tier.color }} />
                      <span className="truncate">{b}</span>
                    </li>
                  ))}
                  {!topBenefits.length && (
                    <li className="text-[10px] text-[#A8A398]">Willkommen im Club!</li>
                  )}
                  {(d.benefits?.length || 0) > 3 && (
                    <li className="text-[9px] uppercase tracking-[0.28em] text-[#575148]">+{d.benefits.length - 3} weitere</li>
                  )}
                </ul>
              </div>
            </div>

            {/* Footer Card */}
            <div className="mt-4 pt-3 border-t border-[#1A1A1A] flex items-center justify-between text-[9px] uppercase tracking-[0.4em] text-[#575148]">
              <span className="flex items-center gap-1"><TicketIcon size={10} /> Beim Besuch vorzeigen</span>
              {d.favorite_movie_title ? (
                <span className="truncate max-w-[140px]" title={d.favorite_movie_title}>♥ {d.favorite_movie_title}</span>
              ) : (
                <span>{new Date().getFullYear()}</span>
              )}
            </div>
          </div>

          {/* Tier-Band unten */}
          <div className="h-1.5" style={{ background: `linear-gradient(90deg, transparent, ${tier.color}, transparent)` }} />
        </div>

        {/* Aktions-Buttons (kompakt) */}
        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            data-testid="wallet-pkpass-btn"
            onClick={openWallet}
            className="flex items-center justify-center gap-2 py-2.5 rounded-full border border-[#2A2A2A] text-[10px] uppercase tracking-[0.28em] hover:border-[#D4AF37] transitions-safe"
          >
            <Wallet size={13} /> Wallet
          </button>
          <button
            data-testid="wallet-share-btn"
            onClick={shareCard}
            className="flex items-center justify-center gap-2 py-2.5 rounded-full border border-[#2A2A2A] text-[10px] uppercase tracking-[0.28em] hover:border-[#D4AF37] transitions-safe"
          >
            <Share2 size={13} /> Teilen
          </button>
        </div>
      </div>
    </div>
  );
}
